// Parent Homework per-homework board (SC-H1b, design §2.3).
// Back → All homework; ring + title + specialist + recurrence; day strip; quiet not-today /
// amber done-today card; tiles (no homework/specialist line), grouped by slot only when the
// homework has >1 slot today. Tile tap → the in-surface exercise screen, as on Today.

import { exerciseSlotsOn } from '../data.js';
import { _findNextAppointment } from '../parent-view.js';
import { dayStripHtml } from './all-homework.js';
import { makeInfoButton, hasHwNote } from './exercise.js';
import {
  _fmtLocal, _countOn, _slotItemsOn, _slotGroups, _slotName,
  _tintFor, _artFor, _dotColor, _dose, _recurrenceLabel, _esc, _nextWeekday,
  tileHtml, wireTiles,
} from './today.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;

// ctx: { homeworks, compMap, childId, H, boardHwId, onBackToAll, onTileTap, sign }
export function renderBoardInto(wrap, ctx) {
  const { homeworks, compMap, H } = ctx;
  const hw = homeworks.find(h => h.id === ctx.boardHwId);
  if (!hw) { ctx.onBackToAll(); return; }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = _fmtLocal(today);
  const cache = H.DB?.specialists || [];
  const specName = hw.specialist_name || cache.find(s => s.id === hw.specialist_id)?.name || 'Specialist';

  // Back to All homework (inline-start chevron; RTL-aware via logical CSS).
  const back = document.createElement('button');
  back.className = 'hwp-back';
  back.innerHTML = '‹ ' + _esc(T('hw5_all_homework'));
  back.onclick = ctx.onBackToAll;
  wrap.appendChild(back);

  // Ring + title + specialist + recurrence
  const cnt = _countOn([hw], compMap, today);
  const due = cnt.total > 0;
  const off = 150.8 * (1 - (due ? cnt.done / cnt.total : 0));
  const titleRow = document.createElement('div');
  titleRow.innerHTML =
    `<div class="hwp-titlerow"><div class="hwp-ring"><svg viewBox="0 0 56 56" width="56" height="56">` +
    `<circle class="c" cx="28" cy="28" r="24"/><circle class="p" cx="28" cy="28" r="24" stroke-dasharray="150.8" stroke-dashoffset="${due ? off : 150.8}"/></svg>` +
    `<b>${due ? cnt.done + '/' + cnt.total : '—'}</b></div>` +
    `<div><h1 class="ff">${_esc(hw.title)}</h1>` +
    `<p><i class="hwp-dot" style="background:${_dotColor(hw.specialist_id)}"></i> ${_esc(specName)} · ${_esc(_recurrenceLabel(hw))}</p></div></div>`;
  // F-104-ALLHW: the same round info button as the exercise screen, only when the specialist left a note; opens the note sheet. Reading it does NOT mark the intro card seen.
  if (hasHwNote(hw)) titleRow.querySelector('.hwp-titlerow').appendChild(makeInfoButton(hw, null));
  wrap.appendChild(titleRow);

  // Day strip (same builder + counts as the card).
  const nextAppt = hw.duration_type === 'next_appointment' ? _findNextAppointment(ctx.childId, hw.specialist_id, H) : null;
  const stripHost = document.createElement('div');
  stripHost.innerHTML = dayStripHtml(hw, compMap, today, nextAppt);
  if (stripHost.firstChild) wrap.appendChild(stripHost.firstChild);

  // Quiet not-today / amber done-today card.
  if (!due) {
    const wd = _nextWeekday(hw);
    const c = document.createElement('div');
    c.className = 'hwp-alldone quiet';
    c.textContent = T('hw5_not_scheduled_today') + (wd ? ' · ' + T('hw5_next_on', { weekday: wd }) : '');
    wrap.appendChild(c);
  } else if (cnt.done === cnt.total) {
    const c = document.createElement('div');
    c.className = 'hwp-alldone';
    c.textContent = '🌟 ' + T('hw5_this_one_done');
    wrap.appendChild(c);
  }

  const items = _slotItemsOn([hw], today); // {hw, ex, slot}
  if (items.length) {
    const multiToday = new Set(items.map(x => x.slot)).size > 1;
    const multiSlotFor = {};
    (hw.exercises || []).forEach(ex => { multiSlotFor[ex.id] = exerciseSlotsOn(hw, ex, today).length > 1; });
    _slotGroups(items).forEach(({ items: arr }) => {
      if (multiToday) {
        const gh = document.createElement('div'); gh.className = 'hwp-grp'; gh.textContent = _slotName(arr[0].slot); wrap.appendChild(gh);
      }
      const holder = document.createElement('div');
      holder.innerHTML = arr.map(x => tileHtml(x.hw, x.ex, x.slot, compMap, todayStr, multiSlotFor[x.ex.id], false)).join('');
      while (holder.firstChild) wrap.appendChild(holder.firstChild);
    });
  } else {
    // Not scheduled today — show the homework's exercises greyed (non-tappable).
    (hw.exercises || []).forEach(ex => {
      const t = document.createElement('div');
      t.className = 'hwp-tile na';
      t.innerHTML = `<div class="hwp-thumb t-${_tintFor(ex.id)}">${_artFor(ex.id)}</div>` +
        `<div class="hwp-body"><h3 class="ff">${_esc(ex.title || T('hw3_exercise'))}</h3><p class="hwp-dose">${_esc(_dose(ex))}</p></div>`;
      wrap.appendChild(t);
    });
  }

  wireTiles(wrap, ctx.onTileTap, ctx.sign);
}
